import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/SessionProvider";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AvatarUpload from "@/components/AvatarUpload";
import { Loader2, Download, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { z } from "zod";

const profileUpdateSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Navn er påkrævet")
    .max(100, "Navn må max være 100 tegn"),
  phone: z.string()
    .trim()
    .regex(/^\+?[0-9]{8,15}$/, "Ugyldigt telefonnummer (8-15 cifre)")
    .min(8, "Telefonnummer skal være mindst 8 cifre")
    .max(15, "Telefonnummer må max være 15 cifre"),
});

export default function Profile() {
  const { user } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    photo_url: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", user?.id)
          .single();

        if (error) throw error;

        if (data) {
          setFormData({
            name: data.name || "",
            email: data.email || user?.email || "",
            phone: data.phone || "",
            photo_url: data.photo_url || "",
          });
        }
      } catch (error: any) {
        console.error("Error fetching profile:", error);
        toast({
          variant: "destructive",
          title: "Fejl",
          description: "Kunne ikke hente profil",
        });
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [user, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      // Validate input before database update
      const validatedData = profileUpdateSchema.parse({
        name: formData.name,
        phone: formData.phone,
      });

      // Update user profile with validated data
      const { error: profileError } = await supabase
        .from("users")
        .update({
          name: validatedData.name,
          phone: validatedData.phone,
          photo_url: formData.photo_url,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      // Update email if changed
      if (formData.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email,
        });

        if (emailError) throw emailError;

        toast({
          title: "Email opdateret",
          description: "Tjek din email for at bekræfte den nye adresse",
        });
      }

      toast({
        title: "Profil opdateret",
        description: "Dine ændringer er gemt",
      });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      
      // Handle Zod validation errors
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast({
          variant: "destructive",
          title: "Valideringsfejl",
          description: firstError.message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Fejl",
          description: error.message || "Kunne ikke opdatere profil",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-2xl">
        <h1 className="text-4xl font-bold mb-8">Min profil</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label>Profilbillede</Label>
            <AvatarUpload
              userId={user!.id}
              currentPhotoUrl={formData.photo_url}
              userName={formData.name}
              onUploadComplete={(url) => setFormData({ ...formData, photo_url: url })}
            />
          </div>

          <div>
            <Label htmlFor="name">Navn</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Dit fulde navn"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="din@email.dk"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Du vil modtage en bekræftelsesmail hvis du ændrer din email
            </p>
          </div>

          <div>
            <Label htmlFor="phone">Telefon</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+45 12 34 56 78"
            />
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Gem ændringer
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/dashboard")}
            >
              Annuller
            </Button>
          </div>
        </form>

        <div className="mt-12 pt-8 border-t space-y-8">
          <div>
            <h2 className="text-xl font-semibold mb-4">Adgangskode</h2>
            <Button
              variant="outline"
              onClick={() => navigate("/reset-password")}
            >
              Skift adgangskode
            </Button>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">Databeskyttelse (GDPR)</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Download dine data eller slet din konto permanent
            </p>
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={async () => {
                  setExportingData(true);
                  try {
                    const { data, error } = await supabase.functions.invoke("export-user-data");
                    if (error) throw error;
                    
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `sharepark-data-${Date.now()}.json`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                    
                    toast({
                      title: "Data eksporteret",
                      description: "Dine data er downloadet",
                    });
                  } catch (error: any) {
                    toast({
                      variant: "destructive",
                      title: "Fejl",
                      description: error.message || "Kunne ikke eksportere data",
                    });
                  } finally {
                    setExportingData(false);
                  }
                }}
                disabled={exportingData}
              >
                {exportingData ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download mine data
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Slet konto
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Er du sikker?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Denne handling kan ikke fortrydes. Dette vil permanent slette din konto
                      og fjerne alle dine data fra vores servere. Du kan ikke slette din konto
                      hvis du har aktive bookinger.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuller</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setDeletingAccount(true);
                        try {
                          const { error } = await supabase.functions.invoke("delete-user-account");
                          if (error) throw error;
                          
                          toast({
                            title: "Konto slettet",
                            description: "Din konto er blevet slettet",
                          });
                          
                          await supabase.auth.signOut();
                          navigate("/");
                        } catch (error: any) {
                          toast({
                            variant: "destructive",
                            title: "Fejl",
                            description: error.message || "Kunne ikke slette konto",
                          });
                        } finally {
                          setDeletingAccount(false);
                        }
                      }}
                      disabled={deletingAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deletingAccount && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Ja, slet min konto
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
